// ============================================================================
// Deep Link Generator — entity lookup for the "detail page" links
// ============================================================================
// The three detail pages in DEEP_LINK_PAGES each need a row id in the URL, and
// an admin has no reason to know one by heart. This module is the only place
// that knows WHICH table backs each of those page keys and how to turn a row
// into something readable, so DeepLinkEntityPicker stays a dumb renderer and
// adding a fourth detail page later is one entry here.
//
// Keyed by DeepLinkPage.key — a key with no entry simply has no picker, and
// the generator falls back to the manual ID field it always had. That is the
// whole compatibility story: this file can only ADD a way to fill the id, it
// can never be the reason a link fails to build.
//
// Which table backs which page (verified against the creator app's routes):
//   campaign-detail → app/campaign/[id] → services/campaigns.getCampaign → `campaigns`
//   job-detail      → app/job/[id]      → `cr_campaigns`
//   offer-detail    → app/offer/[id]    → `cr_hires`
// ============================================================================

import { supabase } from './supabase';

/** One selectable row, flattened for display. */
export interface DeepLinkEntityOption {
  id: string;
  /** Primary line — the thing the admin is looking for by name. */
  title: string;
  /** Secondary line — who it belongs to / what it is attached to. */
  subtitle?: string | null;
  /** Short status word, rendered as a pill. */
  status?: string | null;
  /** Optional thumbnail; falls back to a monogram tile. */
  imageUrl?: string | null;
}

export interface DeepLinkEntitySource {
  /** Plural noun used in the picker's placeholder + empty states. */
  noun: string;
  searchPlaceholder: string;
  /** Newest-first list, filtered by `term` when one is given. */
  search(term: string, limit: number): Promise<DeepLinkEntityOption[]>;
  /** Resolve a single id so a pasted/restored id still shows a real label. */
  byId(id: string): Promise<DeepLinkEntityOption | null>;
  /** Shown under the results when the list is deliberately narrower than the
   *  table — so a missing row reads as "filtered out", not "search is broken". */
  listNote?: string;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: string): boolean {
  return UUID_RE.test(value.trim());
}

/**
 * PostgREST reads `,` `.` `(` `)` as filter syntax inside an `or(...)` group, so
 * a term carrying one would produce a MALFORMED QUERY rather than zero results.
 * Same defensive strip the credit-grant log uses, plus a whitespace collapse so
 * removing punctuation doesn't leave a double space that then matches nothing.
 *
 * Known and accepted limit: a term whose punctuation is also in the stored value
 * ("Acme, Inc.") degrades to a looser match. Safety first — the alternative is a
 * request the API rejects outright.
 */
function escapeLike(term: string): string {
  return term.replace(/[%,().]/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Non-empty, non-blank strings joined by a separator — for subtitle lines. */
function joinParts(parts: (string | null | undefined)[], sep = ' · '): string | null {
  const kept = parts.map((p) => p?.trim()).filter((p): p is string => !!p);
  return kept.length ? kept.join(sep) : null;
}

// ── campaign-detail → public.campaigns ──────────────────────────────────────
// Creator-app campaigns (the Campaigns tab). Labelled by product, since that is
// what the campaign is actually about; the brand is the supporting line.

function campaignOption(row: any): DeepLinkEntityOption {
  return {
    id: row.id,
    title: row.product_name || row.brand_name || 'Untitled campaign',
    subtitle: joinParts([row.product_name ? row.brand_name : null]),
    status: row.status ?? null,
    imageUrl: row.product_image_url ?? null,
  };
}

const CAMPAIGN_COLUMNS = 'id, brand_name, product_name, product_image_url, status, created_at';

const campaignSource: DeepLinkEntitySource = {
  noun: 'campaigns',
  searchPlaceholder: 'Search by product or brand…',
  listNote: "Only active campaigns are listed — a paused or completed one isn't reachable in the app.",
  async search(term, limit) {
    // Mirrors what the creator app actually lists: services/campaigns.ts
    // getCampaigns() filters `status = 'active'`, so anything else would be a
    // link to a screen the creator can't reach from the app.
    let query = supabase.from('campaigns').select(CAMPAIGN_COLUMNS).eq('status', 'active');
    const q = escapeLike(term);
    if (isUuid(term)) query = query.eq('id', term.trim());
    else if (q) query = query.or(`product_name.ilike.%${q}%,brand_name.ilike.%${q}%`);
    const { data, error } = await query.order('created_at', { ascending: false }).limit(limit);
    if (error) throw error;
    return (data ?? []).map(campaignOption);
  },
  async byId(id) {
    const { data, error } = await supabase.from('campaigns').select(CAMPAIGN_COLUMNS).eq('id', id).maybeSingle();
    if (error) throw error;
    return data ? campaignOption(data) : null;
  },
};

// ── job-detail → public.cr_campaigns ────────────────────────────────────────
// Hiring campaigns ("jobs"). These carry their own name AND a product name, so
// both are worth showing; the brand comes from the embedded brand_configs row.

function jobOption(row: any): DeepLinkEntityOption {
  const brand = row.brand_configs?.brand_name ?? null;
  return {
    id: row.id,
    title: row.name || row.product_name || 'Untitled job',
    subtitle: joinParts([brand, row.name ? row.product_name : null]),
    status: row.status ?? null,
    imageUrl: row.product_image_url ?? null,
  };
}

const JOB_COLUMNS = 'id, name, product_name, product_image_url, status, created_at, brand_configs(brand_name)';

const jobSource: DeepLinkEntitySource = {
  noun: 'jobs',
  searchPlaceholder: 'Search by job or product name…',
  listNote: "Only active jobs are listed — paused and archived jobs don't appear in Browse.",
  async search(term, limit) {
    // Same filter BrowseJobs.tsx applies (`status = 'active'`); cr_campaigns
    // statuses are active | paused | archived.
    let query = supabase.from('cr_campaigns').select(JOB_COLUMNS).eq('status', 'active');
    const q = escapeLike(term);
    if (isUuid(term)) query = query.eq('id', term.trim());
    else if (q) query = query.or(`name.ilike.%${q}%,product_name.ilike.%${q}%`);
    const { data, error } = await query.order('created_at', { ascending: false }).limit(limit);
    if (error) throw error;
    return (data ?? []).map(jobOption);
  },
  async byId(id) {
    const { data, error } = await supabase.from('cr_campaigns').select(JOB_COLUMNS).eq('id', id).maybeSingle();
    if (error) throw error;
    return data ? jobOption(data) : null;
  },
};

// ── offer-detail → public.cr_hires ──────────────────────────────────────────
// One creator's engagement on one job. The creator is what an admin searches
// for, and both the creator and the job live on EMBEDDED rows — PostgREST's
// `or()` can't filter across an embed, so the ids are resolved first and ORed
// in as `in.(…)` (same two-step the Agencies list uses for its brand search).

function offerOption(row: any): DeepLinkEntityOption {
  const creator = row.roster_creators?.name
    || (row.roster_creators?.tiktok_username ? `@${row.roster_creators.tiktok_username}` : null);
  const job = row.cr_campaigns?.name || row.cr_campaigns?.product_name || null;
  return {
    id: row.id,
    title: creator || 'Unknown creator',
    subtitle: joinParts([job, row.package_size ? `${row.package_size} videos` : null]),
    status: row.status ?? null,
    imageUrl: null,
  };
}

const OFFER_COLUMNS =
  'id, status, package_size, created_at, roster_creators(name, tiktok_username), cr_campaigns(name, product_name)';

const offerSource: DeepLinkEntitySource = {
  noun: 'offers',
  searchPlaceholder: 'Search by creator or job…',
  // Deliberately NOT narrowed to active jobs, unlike the two lists above. An
  // offer is one creator's own engagement, and app/offer/[id].tsx opens it on
  // its id alone with no campaign-status gate — so a hire on a job that was
  // paused afterwards is still a page that creator can open. Each row shows its
  // own status instead, so a dead offer is visible rather than hidden.
  async search(term, limit) {
    let query = supabase.from('cr_hires').select(OFFER_COLUMNS);
    const q = escapeLike(term);

    if (isUuid(term)) {
      query = query.eq('id', term.trim());
    } else if (q) {
      const [creatorsRes, jobsRes] = await Promise.all([
        supabase.from('roster_creators').select('id')
          .or(`name.ilike.%${q}%,tiktok_username.ilike.%${q}%,email.ilike.%${q}%`)
          .limit(200),
        supabase.from('cr_campaigns').select('id')
          .or(`name.ilike.%${q}%,product_name.ilike.%${q}%`)
          .limit(200),
      ]);
      const creatorIds = (creatorsRes.data ?? []).map((r: any) => r.id);
      const jobIds = (jobsRes.data ?? []).map((r: any) => r.id);
      // Nothing matched on either side — return early rather than sending an
      // empty `or()`, which PostgREST rejects (and which would otherwise read
      // as "no filter" and list every offer for a term that matched nothing).
      if (!creatorIds.length && !jobIds.length) return [];
      const parts: string[] = [];
      if (creatorIds.length) parts.push(`creator_id.in.(${creatorIds.join(',')})`);
      if (jobIds.length) parts.push(`campaign_id.in.(${jobIds.join(',')})`);
      query = query.or(parts.join(','));
    }

    const { data, error } = await query.order('created_at', { ascending: false }).limit(limit);
    if (error) throw error;
    return (data ?? []).map(offerOption);
  },
  async byId(id) {
    const { data, error } = await supabase.from('cr_hires').select(OFFER_COLUMNS).eq('id', id).maybeSingle();
    if (error) throw error;
    return data ? offerOption(data) : null;
  },
};

/**
 * page key → how to look its rows up. A key that is absent here keeps the
 * plain ID input, so this map is purely additive to the generator.
 */
export const DEEP_LINK_ENTITY_SOURCES: Record<string, DeepLinkEntitySource> = {
  'campaign-detail': campaignSource,
  'job-detail': jobSource,
  'offer-detail': offerSource,
};

export function findEntitySource(pageKey: string | undefined): DeepLinkEntitySource | undefined {
  return pageKey ? DEEP_LINK_ENTITY_SOURCES[pageKey] : undefined;
}
